import type { Translate } from "@khazzin/i18n";
import { CloudOff, type LucideIcon, RefreshCw, UploadCloud, Wifi } from "lucide-react";
import { Icon } from "../icons/Icon";
import { cx, FOCUS_RING, TONE_CLASSES, type Tone } from "../styles";

/**
 * Where the Driver App's outbox stands, as one chip in the top bar (`10/04` §7,
 * DA-10-23): Online, Syncing, n queued, or Offline.
 *
 * Offline wins over everything, because it is the one the driver can act on
 * (find signal). Syncing next, then the queue, then all clear. The chip is a
 * button when `onOpen` is given, opening the sync list.
 */

export type SyncState = "online" | "syncing" | "offline";

export interface SyncStatusChipProps {
	state: SyncState;
	/** Commands captured and not yet applied by the server. */
	queued: number;
	t: Translate;
	onOpen?: () => void;
}

const LOOKS: Record<"online" | "syncing" | "queued" | "offline", { tone: Tone; icon: LucideIcon }> =
	{
		online: { tone: "success", icon: Wifi },
		syncing: { tone: "info", icon: RefreshCw },
		queued: { tone: "warning", icon: UploadCloud },
		offline: { tone: "neutral", icon: CloudOff },
	};

/** Which of the four looks a state and a queue give. */
export function syncLook(state: SyncState, queued: number): keyof typeof LOOKS {
	if (state !== "online") {
		return state;
	}
	return queued > 0 ? "queued" : "online";
}

export function SyncStatusChip({ state, queued, t, onOpen }: SyncStatusChipProps) {
	const look = syncLook(state, queued);
	const { tone, icon } = LOOKS[look];
	// Offline with work waiting says both: the count is what a driver checks.
	const label =
		look === "offline" && queued > 0
			? t("ui.sync.offlineQueued", { count: queued })
			: t(`ui.sync.${look}`, { count: queued });
	const className = cx(
		"inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[12px] font-bold max-sm:min-h-12",
		TONE_CLASSES[tone],
	);
	const body = (
		<>
			<Icon
				as={icon}
				size={14}
				className={look === "syncing" ? "motion-safe:animate-spin" : undefined}
			/>
			{label}
		</>
	);
	if (onOpen === undefined) {
		return (
			<span role="status" className={className}>
				{body}
			</span>
		);
	}
	return (
		<button type="button" onClick={onOpen} className={cx(className, FOCUS_RING, "cursor-pointer")}>
			{body}
		</button>
	);
}
